import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Upload, Copy, X, Check } from 'lucide-react';
import { useCurrentPatient } from '../contexts/CurrentPatientContext';
import API_BASE_URL from '../config/api';

function PatientBanner() {
  const { currentPatient, setCurrentPatient, clearCurrentPatient } = useCurrentPatient();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Auto-fetch full details if we only have partial data (e.g. from diary click)
  useEffect(() => {
    if (!currentPatient?.id) return;
    // If we already have phone or email, we have full data
    if (currentPatient.phone || currentPatient.email || currentPatient.dateOfBirth) return;

    let cancelled = false;
    const fetchFull = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/patient-files/${currentPatient.id}`);
        if (!cancelled && res.data) {
          setCurrentPatient({
            id: res.data.id,
            name: res.data.name,
            nhi: res.data.nhi,
            dateOfBirth: res.data.dateOfBirth,
            phone: res.data.phone,
            email: res.data.email,
            address: res.data.address
          });
        }
      } catch {
        // Partial data is fine
      }
    };
    fetchFull();
    return () => { cancelled = true; };
  }, [currentPatient?.id]);

  if (!currentPatient) return null;

  const initials = (currentPatient.name || '')
    .split(' ')
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const formatDob = (dob) => {
    if (!dob) return null;
    try {
      const d = new Date(dob);
      if (isNaN(d.getTime())) return dob;
      return d.toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dob;
    }
  };

  const handleCopyNhi = async () => {
    if (!currentPatient.nhi) return;
    try {
      await navigator.clipboard.writeText(currentPatient.nhi);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select-and-copy not needed for modern browsers
    }
  };

  const btnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: '#cbd5e1',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap'
  };

  return (
    <div style={{
      backgroundColor: '#1e3a5f',
      color: '#e2e8f0',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '0 16px',
      height: '48px',
      fontSize: '13px',
      borderBottom: '1px solid #2d4a6f',
      flexShrink: 0
    }}>
      {/* Initials badge */}
      <div style={{
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        backgroundColor: '#3b82f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        fontSize: '12px',
        color: 'white',
        flexShrink: 0
      }}>
        {initials}
      </div>

      {/* Name */}
      <span style={{ fontWeight: '600', color: 'white', whiteSpace: 'nowrap' }}>
        {currentPatient.name}
      </span>

      {/* Separator */}
      <span style={{ color: '#475569' }}>|</span>

      {/* Details */}
      {currentPatient.nhi && (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span style={{ color: '#94a3b8' }}>NHI:</span> {currentPatient.nhi}
        </span>
      )}

      {currentPatient.dateOfBirth && (
        <>
          <span style={{ color: '#475569' }}>|</span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <span style={{ color: '#94a3b8' }}>DOB:</span> {formatDob(currentPatient.dateOfBirth)}
          </span>
        </>
      )}

      {currentPatient.phone && (
        <>
          <span style={{ color: '#475569' }}>|</span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <span style={{ color: '#94a3b8' }}>Ph:</span> {currentPatient.phone}
          </span>
        </>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Action buttons */}
      <button
        onClick={() => navigate(`/patient-file/${currentPatient.id}`)}
        style={btnStyle}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'white'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
        title="View Patient File"
      >
        <FileText size={14} />
        View File
      </button>

      <button
        onClick={() => navigate(`/patient-file/${currentPatient.id}?tab=letters`)}
        style={btnStyle}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'white'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
        title="View Letters"
      >
        <FileText size={14} />
        Letters
      </button>

      <button
        onClick={() => {
          navigate(`/patient-file/${currentPatient.id}?tab=documents`);
        }}
        style={btnStyle}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'white'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
        title="Upload Document"
      >
        <Upload size={14} />
        Upload
      </button>

      {currentPatient.nhi && (
        <button
          onClick={handleCopyNhi}
          style={{
            ...btnStyle,
            color: copied ? '#4ade80' : '#cbd5e1',
            borderColor: copied ? '#4ade80' : 'rgba(255,255,255,0.25)'
          }}
          onMouseEnter={(e) => { if (!copied) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'white'; } }}
          onMouseLeave={(e) => { if (!copied) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; } }}
          title="Copy NHI to clipboard"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied!' : 'Copy NHI'}
        </button>
      )}

      {/* Close button */}
      <button
        onClick={clearCurrentPatient}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          border: 'none',
          borderRadius: '4px',
          backgroundColor: 'transparent',
          color: '#94a3b8',
          cursor: 'pointer',
          transition: 'all 0.15s',
          flexShrink: 0
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#f87171'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
        title="Dismiss patient"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default PatientBanner;
